import React, { useState, useEffect, useCallback, useRef } from 'react';
import { calcPts, ptLabel } from './logic';
import { Avatar } from './components';

const BASE_URL = 'https://hockey.highlightly.net';

const FLAG_MAP = {
  // Czech
  'czech republic':'🇨🇿','czech':'🇨🇿','czechia':'🇨🇿',
  // Slovak
  'slovensko':'🇸🇰','slovakia':'🇸🇰','slovak republic':'🇸🇰',
  // German
  'německo':'🇩🇪','nemecko':'🇩🇪','germany':'🇩🇪',
  // Swiss
  'švýcarsko':'🇨🇭','svycarsko':'🇨🇭','switzerland':'🇨🇭',
  // Norway
  'norsko':'🇳🇴','norway':'🇳🇴',
  // Sweden
  'švédsko':'🇸🇪','svedsko':'🇸🇪','sweden':'🇸🇪',
  // Finland
  'finsko':'🇫🇮','finland':'🇫🇮',
  // Canada
  'kanada':'🇨🇦','canada':'🇨🇦',
  // USA
  'usa':'🇺🇸','united states':'🇺🇸',
  // Latvia
  'lotyšsko':'🇱🇻','lotyssko':'🇱🇻','latvia':'🇱🇻',
  // Denmark
  'dánsko':'🇩🇰','dansko':'🇩🇰','denmark':'🇩🇰',
  // Austria
  'rakousko':'🇦🇹','austria':'🇦🇹',
  // Hungary
  'maďarsko':'🇭🇺','madarsko':'🇭🇺','hungary':'🇭🇺',
  // France
  'francie':'🇫🇷','france':'🇫🇷',
  // Kazakhstan
  'kazachstán':'🇰🇿','kazachstan':'🇰🇿','kazakhstan':'🇰🇿',
  // Great Britain
  'velká británie':'🇬🇧','velka britanie':'🇬🇧','great britain':'🇬🇧','united kingdom':'🇬🇧',
  // Belarus
  'bělorusko':'🇧🇾','belorusko':'🇧🇾','belarus':'🇧🇾',
  // Italy
  'itálie':'🇮🇹','italie':'🇮🇹','italy':'🇮🇹',
  // Slovenia
  'slovinsko':'🇸🇮','slovenia':'🇸🇮',
  // Russia
  'rusko':'🇷🇺','russia':'🇷🇺',
  // Poland
  'polsko':'🇵🇱','poland':'🇵🇱',
  // Japan
  'japonsko':'🇯🇵','japan':'🇯🇵',
  // Ukraine
  'ukrajina':'🇺🇦','ukraine':'🇺🇦',
  // Others
  'španělsko':'🇪🇸','spain':'🇪🇸',
  'rumunsko':'🇷🇴','romania':'🇷🇴',
  'chorvatsko':'🇭🇷','croatia':'🇭🇷',
  'estonsko':'🇪🇪','estonia':'🇪🇪',
  'litva':'🇱🇹','lithuania':'🇱🇹',
  'norsko':'🇳🇴','norway':'🇳🇴',
};

function getFlag(o) {
  if (!o) return '🏒';
  const k = o.toLowerCase().trim();
  if (FLAG_MAP[k]) return FLAG_MAP[k];
  for (const [n, f] of Object.entries(FLAG_MAP)) { if (k.includes(n)) return f; }
  return '🏒';
}

export default function LiveWidget({ match, matches, tips, members, apiKey, isAdmin, onSaveResult, manualScore }) {
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(false);
  // liveMatchId = ID zápasu pro který jsou aktuální live data
  // liveMatchTips = tipy načtené v momentě kdy live data přišla
  const [liveMatchId, setLiveMatchId] = useState(null);
  const [liveMatchTips, setLiveMatchTips] = useState([]);
  const timer = useRef(null);

  const fetchLive = useCallback(async () => {
    if (!apiKey || !match) return;
    setLoading(true); setErr(null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const res = await fetch(
        `${BASE_URL}/matches?date=${today}&leagueName=World Championship&limit=50`,
        { headers: { 'x-rapidapi-key': apiKey } }
      );
      const data = await res.json();
      const games = data.data || [];
      const game = games.find(g => {
        const h = (g.homeTeam?.name || '').toLowerCase();
        const a = (g.awayTeam?.name || '').toLowerCase();
        return h.includes('czech') || a.includes('czech');
      });
      if (game) {
        const czIsHome = (game.homeTeam?.name || '').toLowerCase().includes('czech');
        const scoreStr = game.state?.score?.current || '';
        const parts = scoreStr.split('-').map(s => s.trim());
        const homeScore = parts[0] != null && parts[0] !== '' ? parseInt(parts[0]) : null;
        const awayScore = parts[1] != null && parts[1] !== '' ? parseInt(parts[1]) : null;
        const desc = (game.state?.description || '').toLowerCase();
        const finished = desc.includes('finish') || desc.includes('after');
        const apiOpponent = czIsHome ? game.awayTeam?.name : game.homeTeam?.name;

        // Najdeme správný zápas podle dnešního data
        const targetMatchId = matches.find(m => m.match_date === today)?.id || match.id;

        setLive({
          homeScore: czIsHome ? homeScore : awayScore,
          awayScore: czIsHome ? awayScore : homeScore,
          status: game.state?.description || '',
          finished,
          opponent: apiOpponent,
        });
        setLiveMatchId(targetMatchId);
        setLiveMatchTips(tips.filter(t => t.match_id === targetMatchId));
        setSaved(false);
      } else {
        setLive(null);
        setLiveMatchId(null);
        setLiveMatchTips([]);
        setErr('Dnes žádný zápas Česka, čekej a dej si Plzničku 🍺');
      }
    } catch (e) { setErr('Chyba připojení: ' + e.message); }
    setLoading(false);
  }, [apiKey, match, tips, matches]);

  useEffect(() => {
    if (!apiKey || !match) return;
    fetchLive();
    timer.current = setInterval(fetchLive, 60000);
    return () => clearInterval(timer.current);
  }, [fetchLive, apiKey, match?.id]);

  useEffect(() => {
    if (!live) { setSaved(false); setLiveMatchId(null); setLiveMatchTips([]); }
  }, [match?.id]);

  const activeScore = manualScore?.home != null ? manualScore : live;

  if (!match && !live) return (
    <div style={widgetBase}>
      <div style={{ textAlign: 'center', color: '#334155', padding: '20px 0', fontSize: 13 }}>
        🏒 Žádný nadcházející zápas
      </div>
    </div>
  );

  const result = activeScore?.homeScore != null
    ? { home: String(activeScore.homeScore), away: String(activeScore.awayScore) }
    : null;

  // Tipy = zamčené při fetchi, nebo aktuální match tipy pokud live není
  const displayTips = liveMatchTips.length > 0 ? liveMatchTips : tips.filter(t => t.match_id === match?.id);

  const ranking = result && !(result.home === '0' && result.away === '0' && !live?.finished)
    ? displayTips
        .filter(t => t.home != null)
        .map(t => ({ t, member: members.find(m => m.name === t.member_name) || { name: t.member_name }, p: calcPts({ home: String(t.home), away: String(t.away) }, result) }))
        .sort((a, b) => b.p - a.p)
    : [];

  const isFinished = activeScore?.finished;
  const canSave = isAdmin && result && isFinished && !saved && liveMatchId;

  const opponentName = live?.opponent || match?.opponent || '';
  const opponentFlag = getFlag(opponentName);

  return (
    <div style={widgetBase}>
      <div style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, background: 'radial-gradient(circle,#3B82F618,transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />

      {/* Status row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: isFinished ? '#34D399' : result ? '#EF4444' : '#475569',
          animation: result && !isFinished ? 'livePulse 1.2s ease-in-out infinite' : 'none',
        }} />
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: isFinished ? '#34D399' : result ? '#EF4444' : '#475569' }}>
          {isFinished ? 'Konec zápasu' : result ? (activeScore?.status || 'Live') : apiKey ? (loading ? 'Načítám…' : 'Čekám na zápas') : 'Nadcházející zápas'}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 5 }}>
          {!apiKey && <span style={{ fontSize: 9, color: '#334155' }}>bez API klíče</span>}
          {apiKey && <button onClick={fetchLive} style={{ background: 'none', border: 'none', color: '#334155', cursor: 'pointer', fontSize: 15 }}>⟳</button>}
          {isFinished && saved && (
            <button onClick={() => { setLive(null); setLiveMatchId(null); setLiveMatchTips([]); setSaved(false); }}
              style={{ background: 'none', border: '1px solid #334155', borderRadius: 6, color: '#475569', cursor: 'pointer', fontSize: 10, padding: '2px 6px' }}>
              Další zápas →
            </button>
          )}
        </div>
      </div>

      {err && <div style={{ fontSize: 11, color: '#F87171', marginBottom: 8 }}>{err}</div>}

      {/* Scoreboard */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: canSave || ranking.length > 0 ? 14 : 0 }}>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontSize: 32, marginBottom: 4 }}>🇨🇿</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#F1F5F9' }}>Česko</div>
        </div>
        <div style={{ textAlign: 'center', padding: '0 12px' }}>
          {result
            ? <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 48, lineHeight: 1, letterSpacing: 3 }}>
                <span style={{ color: +result.home > +result.away ? '#34D399' : +result.home < +result.away ? '#F87171' : '#F1F5F9' }}>{result.home}</span>
                <span style={{ color: '#334155', fontSize: 36, margin: '0 4px' }}>:</span>
                <span style={{ color: +result.away > +result.home ? '#34D399' : +result.away < +result.home ? '#F87171' : '#F1F5F9' }}>{result.away}</span>
              </div>
            : <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 40, color: '#1E293B', letterSpacing: 4 }}>– : –</div>
          }
          <div style={{ fontSize: 9, color: '#334155', textTransform: 'uppercase', letterSpacing: 2, marginTop: 2 }}>{match?.phase || ''}</div>
        </div>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontSize: 32, marginBottom: 4 }}>{opponentFlag}</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B' }}>{opponentName}</div>
        </div>
      </div>

      {canSave && (
        <button
          onClick={() => { onSaveResult({ ...result, matchId: liveMatchId }); setSaved(true); }}
          style={{ width: '100%', marginBottom: 12, background: 'linear-gradient(90deg,#16A34A,#15803D)', border: 'none', borderRadius: 10, padding: 12, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          ✅ Uložit výsledek {result.home}:{result.away} do tipovačky
        </button>
      )}
      {saved && <div style={{ textAlign: 'center', fontSize: 12, color: '#34D399', fontWeight: 600, marginBottom: 12 }}>✓ Výsledek uložen!</div>}

      {ranking.length > 0 && (
        <>
          <div style={{ fontSize: 10, color: '#334155', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8 }}>
            {isFinished ? 'Výsledné pořadí' : 'Aktuální pořadí tipů'}
          </div>
          {ranking.map(({ t, member, p }, i) => {
            const pl = ptLabel(p);
            return (
              <div key={t.member_name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, background: i === 0 ? '#FFD60A0A' : 'rgba(255,255,255,0.02)', borderRadius: 8, padding: '6px 10px', border: i === 0 ? '1px solid #FFD60A33' : '1px solid transparent' }}>
                <span style={{ width: 20, textAlign: 'center', fontSize: 14 }}>{['🥇','🥈','🥉'][i] || `${i+1}.`}</span>
                <Avatar member={member} size={26} />
                <div style={{ flex: 1, fontSize: 12, fontWeight: i === 0 ? 700 : 400, color: '#F1F5F9' }}>{t.member_name}</div>
                <div style={{ fontSize: 12, color: '#475569' }}>tip: <b style={{ color: '#F1F5F9' }}>{t.home}:{t.away}</b></div>
                <div style={{ fontSize: 12, fontWeight: 700, color: pl.c }}>{p}b {pl.e}</div>
              </div>
            );
          })}
        </>
      )}

      {!result && ranking.length === 0 && (
        <div style={{ textAlign: 'center', fontSize: 12, color: '#1E293B', padding: '6px 0' }}>
          {apiKey ? 'Čekám na start zápasu…' : 'Přidej API klíč pro automatické live skóre'}
        </div>
      )}
    </div>
  );
}

const widgetBase = {
  background: 'linear-gradient(135deg,#0F172A,#1E293B)',
  border: '1px solid #334155', borderRadius: 16,
  padding: '16px 14px', marginBottom: 14, position: 'relative', overflow: 'hidden',
};
